import React, { useState, useEffect } from 'react';
import {
  Box, IconButton, Tooltip, MenuItem, TextField, FormControl,
  InputLabel, Select, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import Datatable from '../../components/Datatable';
import FormDialog from '../../components/Datatable/FormDialog';
import { get_one, create, update, delete_one } from '../../services/api';
import { useAuth } from '../../components/AuthProvider';
import { useFormError } from '../../components/Datatable/FormErrorProvider';

const EMPTY = {
  name: '', type: 'ssh_proxy',
  proxy_host: '', proxy_port: 22, proxy_username: '', proxy_password: '', max_sessions: 5,
  local_ip: '', remote_ip: '', tunnel_ip: '', gre_key: '', interface_name: '',
};

export default function Tunnel() {
  const auth = useAuth();
  const { setErrors } = useFormError();
  const [refresh, setRefresh] = useState(false);
  const [form, setForm] = useState({ open: false, id: null });
  const [state, setState] = useState(EMPTY);

  useEffect(() => {
    if (form.open && form.id) {
      get_one('/hermes/tunnel', form.id).then((r) => setState({ ...EMPTY, ...r.data.data }));
    } else if (form.open) {
      setState(EMPTY);
    }
  }, [form]);

  const ch = (e) => setState((s) => ({ ...s, [e.target.name]: e.target.value }));

  const submit = () => {
    const req = form.id
      ? update('/hermes/tunnel', form.id, state)
      : create('/hermes/tunnel/create', state);
    req.then((res) => {
      if (res?.data?.errors) {
        setErrors(res.data.errors);
        return;
      }
      setForm({ open: false, id: null });
      setRefresh((r) => !r);
    });
  };

  const remove = (id) => {
    if (!window.confirm('Remover este túnel?')) return;
    delete_one('/hermes/tunnel', id).then(() => setRefresh((r) => !r));
  };

  const columns = [
    { name: 'id', label: 'ID', options: { display: false, filter: false } },
    { name: 'name', label: 'Nome' },
    {
      name: 'type', label: 'Tipo',
      options: {
        customBodyRender: (v) => (
          <Chip size="small" label={v === 'gre' ? 'GRE' : 'SSH Proxy'}
                color={v === 'gre' ? 'secondary' : 'primary'} />
        ),
      },
    },
    { name: 'proxy_host', label: 'Proxy host' },
    { name: 'interface_name', label: 'Interface GRE' },
    { name: 'max_sessions', label: 'Máx. sessões' },
    {
      name: 'actions', label: 'Ações',
      options: {
        filter: false, sort: false,
        customBodyRender: (_, meta) => {
          const id = meta.rowData[0];
          return (
            <Box display="flex" gap={0.25}>
              <Tooltip title="Editar">
                <span>
                  <IconButton size="small" disabled={!auth.hasPerm('tunnel.update')}
                              onClick={() => setForm({ open: true, id })}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remover">
                <span>
                  <IconButton size="small" disabled={!auth.hasPerm('tunnel.delete')}
                              onClick={() => remove(id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
    },
  ];

  return (
    <>
      <FormDialog
        open={form.open}
        title={form.id ? 'Editar túnel' : 'Novo túnel'}
        handlerClose={() => setForm({ open: false, id: null })}
        handlerSubmit={submit}
        btnSubmit={form.id ? 'Atualizar' : 'Salvar'}
      >
        <TextField name="name" label="Nome" value={state.name || ''} onChange={ch}
                   fullWidth margin="dense" />
        <FormControl fullWidth margin="dense">
          <InputLabel id="tunnel-type">Tipo</InputLabel>
          <Select labelId="tunnel-type" label="Tipo" value={state.type || 'ssh_proxy'}
                  onChange={(e) => setState((s) => ({ ...s, type: e.target.value }))}>
            <MenuItem value="ssh_proxy">SSH Proxy (bastion / ProxyJump)</MenuItem>
            <MenuItem value="gre">Túnel GRE</MenuItem>
          </Select>
        </FormControl>

        {state.type === 'ssh_proxy' && (
          <>
            <TextField name="proxy_host" label="Host do proxy" value={state.proxy_host || ''}
                       onChange={ch} fullWidth margin="dense" />
            <TextField name="proxy_port" label="Porta SSH" type="number" value={state.proxy_port || ''}
                       onChange={ch} fullWidth margin="dense" />
            <TextField name="proxy_username" label="Usuário" value={state.proxy_username || ''}
                       onChange={ch} fullWidth margin="dense" />
            <TextField name="proxy_password" label="Senha" type="password" value={state.proxy_password || ''}
                       onChange={ch} fullWidth margin="dense" />
            <TextField name="max_sessions" label="Máx. sessões simultâneas" type="number"
                       value={state.max_sessions || ''} onChange={ch} fullWidth margin="dense"
                       helperText="Protege o bastion contra MaxSessions/MaxStartups do sshd." />
          </>
        )}

        {state.type === 'gre' && (
          <>
            <TextField name="interface_name" label="Nome da interface (ex: gre1)"
                       value={state.interface_name || ''} onChange={ch} fullWidth margin="dense" />
            <TextField name="local_ip" label="IP local" value={state.local_ip || ''}
                       onChange={ch} fullWidth margin="dense" />
            <TextField name="remote_ip" label="IP remoto (peer)" value={state.remote_ip || ''}
                       onChange={ch} fullWidth margin="dense" />
            <TextField name="tunnel_ip" label="IP do túnel (ex: 10.255.0.1/30)"
                       value={state.tunnel_ip || ''} onChange={ch} fullWidth margin="dense" />
            <TextField name="gre_key" label="GRE key (opcional)" type="number"
                       value={state.gre_key || ''} onChange={ch} fullWidth margin="dense" />
          </>
        )}
      </FormDialog>

      <Datatable
        refresh={refresh}
        handlerRefresh={() => setRefresh((r) => !r)}
        title="Conectividade (Túneis)"
        endpoint="/hermes/tunnel"
        options={{
          customToolbar: () => (
            <Tooltip title="Adicionar túnel">
              <IconButton disabled={!auth.hasPerm('tunnel.create')}
                          onClick={() => setForm({ open: true, id: null })}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          ),
          customToolbarSelect: () => '',
        }}
        columns={columns}
      />
    </>
  );
}
